import {
  Incoterm,
  InvoiceMode,
  OrderAction,
  OrderItemChildType,
  OrderStatus,
  OrderType,
  PricingMode,
  PricingPercentageMode,
} from "@prisma/client";
import dayjs from "dayjs";
import CustomParseFormat from "dayjs/plugin/customParseFormat";
import { z } from "zod";

import { isReservedLotName } from "~/shared/constants/lot.constants";
import { BolDocumentPdfInputSchema } from "~/shared/documents/schemas/bol-document-pdf-input.schema";
import {
  SalesOrderConfirmationLineItemInputSchema,
  SalesOrderConfirmationModalInputSchema,
} from "~/shared/documents/schemas/oc-document-config.schema";
import { PackingSlipPdfInputSchema } from "~/shared/documents/schemas/packing-slip-document-config.schema";
import { PickUpRequestPdfInputSchema } from "~/shared/documents/schemas/pick-up-request-document-config.schema";
import { WarehouseReleasePdfInputSchema } from "~/shared/documents/schemas/warehouse-release-document-config.schema";
import { PurchaseOrderItemSourceMetadataSchema, SalesOrderShipmentMetadataSchema } from "~/shared/orders/shipment-metadata";
import { DocumentAddressSchema, DocumentShippableEntitySchema } from "~/utils/address-formatters";
import { isSupportedCurrencyCode, normalizeCurrencyCode } from "~/utils/currency-codes";
import { decimal, PK, requiredUuid, stringEmptyForcedNull, uuid } from "~/utils/schemas";
import { Decimal } from "~/utils/types/decimal";

const OptionalTransactionCurrencyCodeSchema = z
  .string()
  .trim()
  .transform(normalizeCurrencyCode)
  .refine(code => code.length === 0 || isSupportedCurrencyCode(code), "Transaction currency must be a valid ISO 4217 currency code.")
  .transform(code => (code.length > 0 ? code : undefined))
  .optional()
  .nullable();

/** Shared order date validation messages; aligned with UI labels (Deliver By, Scheduled Ship Date) and inclusive >= semantics. */
export const ORDER_DATE_MESSAGES = {
  scheduledShipDateOnOrAfterOrderDate: "Scheduled Ship Date must be on or after Order Date",
  deliverByOnOrAfterOrderDate: "Deliver By must be on or after Order Date",
  deliverByOnOrAfterScheduledShipDate: "Deliver By must be on or after Scheduled Ship Date",
  actualShipDateOnOrAfterOrderDate: "Actual Ship Date must be on or after Order Date",
  apptEndAtOrAfterApptStart: "Appt. End must be at or after Appt. Start",
} as const;

dayjs.extend(CustomParseFormat);

const calDayOnOrAfter = (a: Date | null | undefined, b: Date | null | undefined, { allowNull }: { allowNull: boolean }): boolean => {
  if (a == null || b == null) {
    return allowNull;
  }
  return !dayjs(a).startOf("day").isBefore(dayjs(b).startOf("day"));
};

export const OrderHeaderSchema = z
  .object({
    assignedToId: requiredUuid,
    copiedFromId: z.string().uuid().nullish(),
    /** When set, the server atomically promotes this READY extracted order on successful create. */
    extractedOrderId: z.string().uuid().optional(),
    /** When set, the server atomically consumes this PENDING partner Order Intent on successful create. */
    pendingOrderId: z.string().uuid().optional(),
    customerId: z.string().uuid({ message: "Customer is required." }),
    customerServiceRepresentativeId: z.union([stringEmptyForcedNull, uuid()]).nullish(),
    customerOrderNo: stringEmptyForcedNull,
    freightMethod: z.string().nullable(),
    freightTerms: stringEmptyForcedNull,
    incoterm: z.nativeEnum(Incoterm).nullish(),
    invoiceMode: z.nativeEnum(InvoiceMode).optional(),
    orderedAt: z.date({ required_error: "Order Date requires a valid date.", invalid_type_error: "Order Date requires a valid date." }),
    orderedById: z.union([stringEmptyForcedNull, z.string().uuid()]),
    orderNo: z.string(),
    poFreightTerms: stringEmptyForcedNull,
    // DSO header input: saved only on the linked PurchaseOrder, never on Order.
    poFob: stringEmptyForcedNull.optional(),
    purchaseOrderId: z.string().uuid().nullish(),
    requiredBy: z.date().nullable(),
    requiredByEnd: z.string().time().nullable(),
    requiredByStart: z.string().time().nullable(),
    salesPersonId: z.preprocess(
      val => (val == null || (typeof val === "string" && val.length === 0) ? undefined : val),
      z.string({ required_error: "Sales Person is required." }).uuid({ message: "Sales Person is required." }),
    ),
    shipBy: z.date().nullable(),
    shipByEnd: z.string().time().nullable(),
    shipByStart: z.string().time().nullable(),
    shipFromLocationId: z.union([requiredUuid, z.literal(null)]),
    shippingCarrier: stringEmptyForcedNull,
    carrierContactId: z.string().uuid().nullish(),
    shippingFob: stringEmptyForcedNull,
    shippingRemarks: stringEmptyForcedNull,
    useCustomShippingInstructions: z.boolean().default(false),
    // DSO non-blind only: transient field, writes to linked PO's remarks
    supplierShippingRemarks: z.string().nullish(),
    // DSO non-blind only: transient field, writes to linked PO's useCustomSupplierShippingInstructions
    useCustomSupplierShippingInstructions: z.boolean().optional(),
    // DSO only: transient field, writes to linked PO's transactionCurrencyCode; never stored on Order
    poTransactionCurrencyCode: OptionalTransactionCurrencyCodeSchema,
    shipToAddressId: z.string().uuid({ message: "Ship To Address is required." }),
    status: z.nativeEnum(OrderStatus),
    supplierId: z.union([requiredUuid, z.literal(null)]),
    supplierTermsOfPaymentId: z.union([requiredUuid, z.literal(null)]),
    termsOfPaymentId: requiredUuid,
    type: z.nativeEnum(OrderType),
    warehouseId: z.string().uuid().nullish(),
    remarks: z.string().nullish(),
    transactionCurrencyCode: OptionalTransactionCurrencyCodeSchema,
  })
  .refine(order => calDayOnOrAfter(order.shipBy, order.orderedAt, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.scheduledShipDateOnOrAfterOrderDate,
    path: ["shipBy"],
  })
  .refine(
    order => order.shipByStart == null || order.shipByEnd == null || dayjs(order.shipByStart, "HH:mm") <= dayjs(order.shipByEnd, "HH:mm"),
    {
      message: ORDER_DATE_MESSAGES.apptEndAtOrAfterApptStart,
      path: ["shipByEnd"],
    },
  )
  .refine(order => calDayOnOrAfter(order.requiredBy, order.orderedAt, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.deliverByOnOrAfterOrderDate,
    path: ["requiredBy"],
  })
  .refine(order => calDayOnOrAfter(order.requiredBy, order.shipBy, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.deliverByOnOrAfterScheduledShipDate,
    path: ["requiredBy"],
  })
  .refine(
    order =>
      order.requiredByStart == null ||
      order.requiredByEnd == null ||
      dayjs(order.requiredByStart, "HH:mm") <= dayjs(order.requiredByEnd, "HH:mm"),
    {
      message: ORDER_DATE_MESSAGES.apptEndAtOrAfterApptStart,
      path: ["requiredByEnd"],
    },
  )
  .refine(order => order.type === OrderType.STOCK_ORDER || order.supplierId !== null, {
    message: "Supplier is required on Direct Ship Orders",
    path: ["supplierId"],
  })
  .refine(order => order.type === OrderType.STOCK_ORDER || order.supplierTermsOfPaymentId !== null, {
    message: "Supplier Terms of Payment is required on Direct Ship Orders",
    path: ["supplierTermsOfPaymentId"],
  });

export type OrderHeaderSchemaType = z.infer<typeof OrderHeaderSchema>;

export const OrderHeaderWithIdSchema = PK.and(OrderHeaderSchema).and(SalesOrderShipmentMetadataSchema.omit({ freightMethod: true }));
export type OrderHeaderWithIdSchemaType = z.infer<typeof OrderHeaderWithIdSchema>;

/**
 * Schema for product line items.
 * Note: excludeFromMargin is intentionally NOT included here.
 * Product items are always included in commission calculations.
 * Only non-product items (fees/services) can be marked as non-commissionable.
 */
export const OrderItemProductSchema = z.object({
  productId: z.string().uuid({ message: "Product is required." }),
  productPackagingId: requiredUuid,
  productQty: decimal({ positive: true }),
  productWeightLbs: decimal().nullable(),
  quantity: decimal({ positive: true }),
  packagePrice: z.number().nonnegative(),
  itemPackageCost: z.number().nonnegative(),
  lotId: z.union([z.literal(null), z.literal(undefined), requiredUuid]),
  invoiceMode: z.nativeEnum(InvoiceMode).optional(),
});

export const OrderItemProductWithIdSchema = PK.merge(OrderItemProductSchema);
export type OrderItemProductWithIdSchemaType = z.infer<typeof OrderItemProductWithIdSchema>;

/** Purchase-side source metadata is accepted only while creating a linked DSO, never persisted on OrderItem. */
export const OrderCreateItemProductWithIdSchema = OrderItemProductWithIdSchema.merge(PurchaseOrderItemSourceMetadataSchema);
export type OrderCreateItemProductWithIdSchemaType = z.infer<typeof OrderCreateItemProductWithIdSchema>;

const orderItemNonProductSchemaBaseSchema = z.object({
  // fields common across variants
  productId: z.literal(null),
  description: z.string().min(1, { message: "Description is required." }),
  quantity: decimal({ positive: true }),
  packagePrice: z.number(),
  itemPackageCost: z.literal(null),
  ledgerAccountId: z.union([z.literal(null), z.literal(undefined), requiredUuid]),
  parentItemId: z.union([z.literal(null), z.literal(undefined), requiredUuid]),
  childType: z.nativeEnum(OrderItemChildType).nullish(),
  orderItemTemplateId: z.union([z.literal(null), z.literal(undefined), requiredUuid]),
  pricingMode: z.nativeEnum(PricingMode).default(PricingMode.FIXED),
  pricingPercentageMode: z.nativeEnum(PricingPercentageMode).nullish(),
  percentage: z.number().nonnegative().nullish(),
  /**
   * If true, this non-product item is excluded from commission report calculations.
   * This field only applies to non-product items (productId === null).
   * For product items, this field is ignored by the commission report.
   */
  nonCommissionable: z.boolean().default(false),
  /**
   * If true, this non-product item is excluded from margin calculations AND commission reports.
   * This field only applies to non-product items (productId === null).
   * For product items, this field is ignored.
   */
  excludeFromMargin: z.boolean().default(false),
  transactionCurrencyCode: OptionalTransactionCurrencyCodeSchema,
});

const orderItemNonProductSchema = PK.merge(orderItemNonProductSchemaBaseSchema).superRefine((data, ctx) => {
  if (data.pricingMode === PricingMode.FIXED) {
    if (new Decimal(data.quantity || 0).lte(0)) {
      ctx.addIssue({
        path: ["quantity"],
        code: z.ZodIssueCode.custom,
        message: "Quantity must be greater than 0 when pricing mode is Fixed.",
      });
    }
  }

  if (data.pricingMode === PricingMode.PERCENTAGE) {
    if (data.pricingPercentageMode == null) {
      ctx.addIssue({
        path: ["pricingPercentageMode"],
        code: z.ZodIssueCode.custom,
        message: "Percentage mode is required when using percentage pricing.",
      });
    }

    if (data.percentage == null || data.percentage <= 0) {
      ctx.addIssue({
        path: ["percentage"],
        code: z.ZodIssueCode.custom,
        message: "Percentage must be greater than 0 when using percentage pricing.",
      });
    }
  }
});

export const OrderItemNonProductWithIdSchema = orderItemNonProductSchema;
export type OrderItemNonProductWithIdSchemaType = z.infer<typeof OrderItemNonProductWithIdSchema>;

export const orderCostBaseSchema = z.object({
  orderItemTemplateId: stringEmptyForcedNull,
  supplierId: uuid().nullish(),
  ledgerAccountId: requiredUuid,
  description: z.string().min(1, "Description is required."),
  quantity: decimal({ positive: true }),
  packagePrice: z.number(),
  parentItemId: z.union([z.literal(null), z.literal(undefined), requiredUuid]),
  childType: z.nativeEnum(OrderItemChildType).nullish(),
  pricingMode: z.nativeEnum(PricingMode).default(PricingMode.FIXED),
  pricingPercentageMode: z.nativeEnum(PricingPercentageMode).nullish(),
  percentage: z.number().nonnegative().nullish(),
  nonCommissionable: z.boolean().default(false),
  excludeFromMargin: z.boolean().default(false),
  transactionCurrencyCode: OptionalTransactionCurrencyCodeSchema,
  /** SC-2115 NO_PERIOD picker: dated-fallback rate lookup date. Ignored when an FX period governs the pair. */
  rateAsOfDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Rate as-of date must use YYYY-MM-DD format.")
    .optional()
    .nullable(),
});

const orderCostSchema = PK.merge(orderCostBaseSchema).superRefine((data, ctx) => {
  if (data.pricingMode === PricingMode.FIXED) {
    if (new Decimal(data.quantity || 0).lte(0)) {
      ctx.addIssue({
        path: ["quantity"],
        code: z.ZodIssueCode.custom,
        message: "Quantity must be greater than 0 when pricing mode is Fixed.",
      });
    }
  }

  if (data.pricingMode === PricingMode.PERCENTAGE) {
    if (data.pricingPercentageMode == null) {
      ctx.addIssue({
        path: ["pricingPercentageMode"],
        code: z.ZodIssueCode.custom,
        message: "Percentage mode is required when using percentage pricing.",
      });
    }

    if (data.percentage == null || data.percentage <= 0) {
      ctx.addIssue({
        path: ["percentage"],
        code: z.ZodIssueCode.custom,
        message: "Percentage must be greater than 0 when using percentage pricing.",
      });
    }
  }
});

export const OrderCostWithIdSchema = orderCostSchema;
export type OrderCostWithIdSchemaType = z.infer<typeof OrderCostWithIdSchema>;

export const OrderSchema = OrderHeaderSchema.and(
  z
    .object({
      productItems: z.array(OrderCreateItemProductWithIdSchema).min(1, { message: "An order must have at least 1 line item." }),
      nonProductItems: z.array(OrderItemNonProductWithIdSchema),
      costs: z.array(OrderCostWithIdSchema),
    })
    .merge(SalesOrderShipmentMetadataSchema),
);
export type OrderSchemaType = z.infer<typeof OrderSchema>;

export const OrderShipmentMetadataUpdateSchema = SalesOrderShipmentMetadataSchema.extend({
  orderId: requiredUuid,
}).strict();
export type OrderShipmentMetadataUpdateSchemaType = z.infer<typeof OrderShipmentMetadataUpdateSchema>;

export const OrderWithIdSchema = PK.and(OrderSchema);
export type OrderWithIdSchemaType = z.infer<typeof OrderWithIdSchema>;

export const OrderActivitySchema = z.object({
  orderId: requiredUuid,
  action: z.nativeEnum(OrderAction),
  comment: stringEmptyForcedNull,
});
export type OrderActivitySchemaType = z.infer<typeof OrderActivitySchema>;

export const OrderSalePriceAmendmentSchema = z
  .object({
    orderId: requiredUuid,
    lineAmendments: z
      .array(
        z.object({
          orderItemId: requiredUuid,
          packagePrice: z.number().finite().nonnegative(),
        }),
      )
      .min(1, { message: "At least one order line price is required." }),
  })
  .superRefine((input, ctx) => {
    const seenIds = new Set<string>();
    input.lineAmendments.forEach((amendment, index) => {
      if (seenIds.has(amendment.orderItemId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineAmendments", index, "orderItemId"],
          message: "Each order line can only be amended once.",
        });
      }
      seenIds.add(amendment.orderItemId);
    });
  });
export type OrderSalePriceAmendmentSchemaType = z.infer<typeof OrderSalePriceAmendmentSchema>;

/** Dedicated boundary for changing only the CSR assigned to an existing Sales Order (SC-2783). */
export const OrderCustomerServiceRepresentativeUpdateSchema = z.object({
  orderId: requiredUuid,
  customerServiceRepresentativeId: uuid().nullable(),
  acknowledgeCsrRemoval: z.literal(true).optional(),
});
export type OrderCustomerServiceRepresentativeUpdateSchemaType = z.infer<typeof OrderCustomerServiceRepresentativeUpdateSchema>;

/** Dedicated boundary for reassigning an existing Sales Order without opening the header (SC-3743). */
export const OrderAssignedToUpdateSchema = z.object({
  orderId: requiredUuid,
  assignedToId: requiredUuid,
});
export type OrderAssignedToUpdateSchemaType = z.infer<typeof OrderAssignedToUpdateSchema>;

export const OrderActivityWithIdSchema = PK.merge(OrderActivitySchema);
export type OrderActivityWithIdSchemaType = z.infer<typeof OrderActivityWithIdSchema>;

export const orderShipmentPlanSchema = z.object({
  shipmentPlan: z.object({
    platforms: z
      .array(
        z.object({
          shippingPlatformId: requiredUuid,
          items: z.array(
            z.object({
              orderItemId: requiredUuid,
              packageCount: z.number().int().min(1, "Package Count is required"),
            }),
          ),
        }),
      )
      .superRefine((platforms, ctx) => {
        platforms.forEach((platform, platformIndex) => {
          if (platform.items.length < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "At least one Order Item is required for this Shipping Platform",
              path: [platformIndex, "shippingPlatformId"],
            });
          }
        });
      }),
  }),
});
export type OrderShipmentPlanType = z.infer<typeof orderShipmentPlanSchema>;

/**
 * SC-4143: a quantity leaving one location. Kept as a decimal string so it round-trips exactly
 * through the stored approval payload; the ship action converts it.
 */
const shipLocationQuantitySchema = z
  .string()
  .trim()
  .refine(value => {
    try {
      const quantity = new Decimal(value);
      return quantity.isFinite() && quantity.gt(0);
    } catch {
      return false;
    }
  }, "Enter a quantity above zero.");

export const OrderShipLotLocationSourcesSchema = z.array(
  z.object({
    lotConsumptionId: requiredUuid,
    sources: z.array(z.object({ warehouseLocationId: requiredUuid, qty: shipLocationQuantitySchema })).min(1),
  }),
);
export type OrderShipLotLocationSources = z.infer<typeof OrderShipLotLocationSourcesSchema>;

/** Unrefined owner fields, also used by the approval payload's JSON representation. */
export const OrderActivityShipInputSchema = OrderActivitySchema.merge(
  z.object({
    roOrderedAt: z.date(),
    shipBy: z.date().nullable(),
    requiredBy: z.date().nullable(),
    /**
     * Actual ship date/time - the exact timestamp when the order is being shipped.
     * Used to set both order.shippedAt and GL transaction postedAt.
     * Preserves time component to avoid timezone edge cases and allow backdating.
     * Form default is set in the ship UI; server receives the submitted instant as-is.
     */
    actualShipDate: z.date(),
    incoterm: z.nativeEnum(Incoterm).nullish(),
    freightMethod: z.string(),
    freightTerms: stringEmptyForcedNull,
    shippingCarrier: z.string().min(1, { message: "Shipping Carrier required" }),
    shippingFob: z.string().min(1, { message: "F.O.B. required" }),
    shippingRemarks: stringEmptyForcedNull,
    useCustomShippingInstructions: z.boolean().default(false),
    useShipmentPlan: z.boolean().optional(),
    useShipmentWeights: z.boolean().optional(),
    grossShipmentWeightOverride: z.preprocess(
      val => (val === null || val === undefined || val === "" ? null : val),
      z.number().positive().nullable().optional(),
    ),
    palletCountOverride: z.preprocess(
      val => (val === null || val === undefined || val === "" ? null : val),
      z.number().int().min(0).nullable().optional(),
    ),
    /** PO Received At date for DSO orders. Used to set LotReceipt.receivedAt when shipping. */
    poReceivedAt: z.date().nullable().optional(),
    /** SC-4143: per lot line, the locations it leaves, for lots held in several locations. */
    lotLocationSources: OrderShipLotLocationSourcesSchema.optional(),
  }),
).merge(orderShipmentPlanSchema);

export const OrderActivityShipSchema = OrderActivityShipInputSchema.refine(
  activity => calDayOnOrAfter(activity.shipBy, activity.roOrderedAt, { allowNull: true }),
  {
    message: ORDER_DATE_MESSAGES.scheduledShipDateOnOrAfterOrderDate,
    path: ["shipBy"],
  },
)
  .refine(activity => calDayOnOrAfter(activity.requiredBy, activity.roOrderedAt, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.deliverByOnOrAfterOrderDate,
    path: ["requiredBy"],
  })
  .refine(activity => calDayOnOrAfter(activity.requiredBy, activity.shipBy, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.deliverByOnOrAfterScheduledShipDate,
    path: ["requiredBy"],
  })
  .refine(activity => calDayOnOrAfter(activity.actualShipDate, activity.roOrderedAt, { allowNull: false }), {
    message: ORDER_DATE_MESSAGES.actualShipDateOnOrAfterOrderDate,
    path: ["actualShipDate"],
  });
export type OrderActivityShipSchemaType = z.infer<typeof OrderActivityShipSchema>;

export const OrderActivityMarkDeliveredSchema = OrderActivitySchema.merge(
  z.object({
    roOrderedAt: z.date(),
    requiredBy: z.date().nullable(),
    /** "Delivered on" org calendar day, sent as start-of-day in org tz; becomes `Order.deliveredAt`, never spread onto Order. */
    deliveredOn: z.date(),
  }),
).refine(activity => calDayOnOrAfter(activity.requiredBy, activity.roOrderedAt, { allowNull: true }), {
  message: ORDER_DATE_MESSAGES.deliverByOnOrAfterOrderDate,
  path: ["requiredBy"],
});
export type OrderActivityMarkDeliveredSchemaType = z.infer<typeof OrderActivityMarkDeliveredSchema>;

export const OrderActivityReverseMarkDeliveredSchema = OrderActivitySchema;
export type OrderActivityReverseMarkDeliveredSchemaType = z.infer<typeof OrderActivityReverseMarkDeliveredSchema>;

export const OrderActivityReverseMarkCompletedSchema = OrderActivitySchema;
export type OrderActivityReverseMarkCompletedSchemaType = z.infer<typeof OrderActivityReverseMarkCompletedSchema>;

/**
 * Print this one with the org's assigned template instead of the built-in document.
 *
 * SC-3763 added it to the bill of lading; SC-3349 gives the same choice to every order document
 * the engine can render. Modal-only and per-print, like the `*PdfInput` blocks beside it —
 * excluded from order persistence in each action, because which engine drew one PDF is not a
 * fact about the order.
 *
 * DEFAULTS FALSE everywhere, so each existing caller — and every direct API call written before
 * this existed — keeps printing the legacy document unchanged.
 */
export const printSourceSchema = z.object({ useOutputTemplate: z.boolean().default(false) });
const shipmentPrintSourceSchema = printSourceSchema.extend({ shipmentId: z.string().uuid().optional() });

export const OrderActivityCreatePickUpRequestSchema = OrderActivitySchema.merge(shipmentPrintSourceSchema)
  .merge(
    z.object({
      roOrderedAt: z.date(),
      shipBy: z.date().nullable(),
      requiredBy: z.date().nullable(),
      pickUpRequestPdfInput: PickUpRequestPdfInputSchema.optional(),
    }),
  )
  .refine(activity => calDayOnOrAfter(activity.shipBy, activity.roOrderedAt, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.scheduledShipDateOnOrAfterOrderDate,
    path: ["shipBy"],
  })
  .refine(activity => calDayOnOrAfter(activity.requiredBy, activity.roOrderedAt, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.deliverByOnOrAfterOrderDate,
    path: ["requiredBy"],
  })
  .refine(activity => calDayOnOrAfter(activity.requiredBy, activity.shipBy, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.deliverByOnOrAfterScheduledShipDate,
    path: ["requiredBy"],
  });
export type OrderActivityCreatePickUpRequestSchemaType = z.infer<typeof OrderActivityCreatePickUpRequestSchema>;

const orderActivityCreatePackingSlipShapeSchema = OrderActivitySchema.merge(
  z.object({
    shippingCarrier: z.string().min(1, { message: "Shipping Carrier required" }),
    shippingRemarks: stringEmptyForcedNull,
    useCustomShippingInstructions: z.boolean().default(false),
    freightTerms: stringEmptyForcedNull,
    roOrderedAt: z.date(),
    shipBy: z.date().nullable(),
    requiredBy: z.date().nullable(),
  }),
);

const withPackingSlipOrderDateRefines = <T extends z.ZodTypeAny>(schema: T) =>
  schema
    .refine(activity => calDayOnOrAfter(activity.shipBy, activity.roOrderedAt, { allowNull: true }), {
      message: ORDER_DATE_MESSAGES.scheduledShipDateOnOrAfterOrderDate,
      path: ["shipBy"],
    })
    .refine(activity => calDayOnOrAfter(activity.requiredBy, activity.roOrderedAt, { allowNull: true }), {
      message: ORDER_DATE_MESSAGES.deliverByOnOrAfterOrderDate,
      path: ["requiredBy"],
    })
    .refine(activity => calDayOnOrAfter(activity.requiredBy, activity.shipBy, { allowNull: true }), {
      message: ORDER_DATE_MESSAGES.deliverByOnOrAfterScheduledShipDate,
      path: ["requiredBy"],
    });

export const OrderActivityCreatePackingSlipSchema = withPackingSlipOrderDateRefines(
  orderActivityCreatePackingSlipShapeSchema.merge(shipmentPrintSourceSchema).merge(
    z.object({
      /** Modal-only PDF inputs (SC-2489) — excluded from order persistence in orderActionCreatePackingSlip. */
      packingSlipPdfInput: PackingSlipPdfInputSchema.optional(),
    }),
  ),
);
export type OrderActivityCreatePackingSlipSchemaType = z.infer<typeof OrderActivityCreatePackingSlipSchema>;

export const OrderActivityCreateBillOfLadingSchema = OrderActivitySchema.merge(shipmentPrintSourceSchema).merge(
  z.object({
    shippingCarrier: z.string().optional(),
    shippingRemarks: stringEmptyForcedNull.optional(),
    useCustomShippingInstructions: z.boolean().default(false),
    freightTerms: stringEmptyForcedNull.optional(),
    roOrderedAt: z.date().optional(),
    /** Legacy modal context; saved Order.shipOnBehalf controls identity validation during generation. */
    roOrderType: z.nativeEnum(OrderType).optional(),
    roAllowThirdPartyPickupOnBol: z.boolean().default(false),
    shipBy: z.date().nullable().optional(),
    shipByStart: z.string().time().nullable().optional(),
    shipByEnd: z.string().time().nullable().optional(),
    requiredBy: z.date().nullable().optional(),
    requiredByStart: z.string().time().nullable().optional(),
    requiredByEnd: z.string().time().nullable().optional(),
    shipFrom: DocumentShippableEntitySchema.nullable().optional(),
    shipTo: DocumentShippableEntitySchema.nullable().optional(),
    useShipmentWeights: z.boolean().optional(),
    grossShipmentWeightOverride: z.preprocess(
      val => (val === null || val === undefined || val === "" ? null : val),
      z.number().positive().nullable().optional(),
    ),
    palletCountOverride: z.preprocess(
      val => (val === null || val === undefined || val === "" ? null : val),
      z.number().int().min(0).nullable().optional(),
    ),
    billOfLadingType: z.enum(["STRAIGHT", "ORDER"]).default("STRAIGHT"),
    /** Modal-only PDF inputs (SC-2343) — excluded from order persistence in orderActionCreateBillOfLading. */
    bolPdfInput: BolDocumentPdfInputSchema.optional(),
  }),
);
export type OrderActivityCreateBillOfLadingSchemaType = z.infer<typeof OrderActivityCreateBillOfLadingSchema>;

export const OrderActivityCreateSalesOrderConfirmationSchema = OrderActivitySchema.merge(printSourceSchema).merge(
  SalesOrderConfirmationModalInputSchema.extend({
    soldToAddress: DocumentAddressSchema.nullish(),
    shipToAddress: DocumentAddressSchema.nullish(),
    lineItems: z.array(SalesOrderConfirmationLineItemInputSchema).optional(),
  }),
);
export type OrderActivityCreateSalesOrderConfirmationSchemaType = z.infer<typeof OrderActivityCreateSalesOrderConfirmationSchema>;

export const OrderActivityCreateWarehouseReleaseSchema = withPackingSlipOrderDateRefines(
  orderActivityCreatePackingSlipShapeSchema.merge(shipmentPrintSourceSchema).merge(
    z.object({
      shipFromAddress: DocumentShippableEntitySchema.nullable().optional(),
      releaseToAddress: DocumentShippableEntitySchema.nullable().optional(),
      shipToAddress: DocumentShippableEntitySchema.nullable().optional(),
      /** Modal-only PDF inputs (SC-2489) — excluded from order persistence in orderActionCreateWarehouseRelease. */
      warehouseReleasePdfInput: WarehouseReleasePdfInputSchema.optional(),
    }),
  ),
);
export type OrderActivityCreateWarehouseReleaseSchemaType = z.infer<typeof OrderActivityCreateWarehouseReleaseSchema>;

/** Pick List schema - simpler than Packing Slip, no carrier/freight validation required */
export const OrderActivityCreatePickListSchema = OrderActivitySchema.merge(shipmentPrintSourceSchema)
  .merge(
    z.object({
      shippingCarrier: z.string().optional(),
      shippingRemarks: stringEmptyForcedNull,
      useCustomShippingInstructions: z.boolean().default(false),
      roOrderedAt: z.date(),
      shipBy: z.date().nullable(),
      requiredBy: z.date().nullable(),
      blankLotLines: z.number().int().min(1).max(10).default(4),
    }),
  )
  .refine(activity => calDayOnOrAfter(activity.shipBy, activity.roOrderedAt, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.scheduledShipDateOnOrAfterOrderDate,
    path: ["shipBy"],
  })
  .refine(activity => calDayOnOrAfter(activity.requiredBy, activity.roOrderedAt, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.deliverByOnOrAfterOrderDate,
    path: ["requiredBy"],
  })
  .refine(activity => calDayOnOrAfter(activity.requiredBy, activity.shipBy, { allowNull: true }), {
    message: ORDER_DATE_MESSAGES.deliverByOnOrAfterScheduledShipDate,
    path: ["requiredBy"],
  });
export type OrderActivityCreatePickListSchemaType = z.infer<typeof OrderActivityCreatePickListSchema>;

export const OrderActivityMarkVoid = OrderActivitySchema;
export type OrderActivityMarkVoidType = z.infer<typeof OrderActivityMarkVoid>;

export const OrderActivityMarkCompleted = OrderActivitySchema;
export type OrderActivityMarkCompletedType = z.infer<typeof OrderActivityMarkCompleted>;

export const OrderActivityVoidInvoice = OrderActivitySchema.merge(
  z.object({
    /**
     * The date to record the void transaction.
     * Defaults to the current date if not provided.
     */
    postedAt: z.date().optional(),
  }),
);
export type OrderActivityVoidInvoiceType = z.infer<typeof OrderActivityVoidInvoice>;

export const OrderActivityLotSchemaLotRecord = z.object({
  id: z.union([z.literal(null), z.literal(undefined), requiredUuid]),
  quantity: decimal({ positive: true }),
});
export type OrderActivityLotSchemaLotRecordType = z.infer<typeof OrderActivityLotSchemaLotRecord>;

export const OrderActivityLotSchema = OrderActivitySchema.merge(
  z.object({
    items: z
      .array(
        PK.merge(
          z.object({
            lots: z.array(OrderActivityLotSchemaLotRecord),
          }),
        ),
      )
      .min(1),
  }),
);
export type OrderActivityLotSchemaType = z.infer<typeof OrderActivityLotSchema>;

/**
 * Input for `orders.actionSubmit` (SC-2781).
 *
 * The two extra fields live here rather than on `OrderActivityLotSchema` because that schema is
 * also the input for EDIT_LOTS, which has no business carrying confirmation fields. Both are
 * optional, so an EDIT_LOTS caller simply omits them — and the service ignores them outside a
 * DRAFT promotion regardless.
 */
export const OrderActionSubmitSchema = OrderActivityLotSchema.merge(
  z.object({
    /**
     * The user attested, in the confirm form, to confirming without a CSR. Records intent for
     * the audit row; it unlocks nothing, since confirming a CSR-less order needs no permission
     * beyond the confirm itself.
     */
    acknowledgeConfirmWithoutCsr: z.boolean().optional(),
    /** Optional, non-authorizing client acknowledgement of currently displayed warning Flags. */
    acknowledgedRegulatoryWarningRuleIds: z
      .array(uuid())
      .refine(ruleIds => new Set(ruleIds).size === ruleIds.length, { message: "Regulatory warning rule IDs must be unique." })
      .optional(),
    /**
     * A CSR chosen inline while confirming, applied as part of the same transaction so the form
     * never has to save and refetch mid-flow.
     *
     * Assign-only and deliberately NOT nullable: this feature never needs to clear a CSR, and a
     * nullable field would quietly make confirm a CSR-clearing endpoint — which belongs to
     * SC-2783, behind its own controls. An empty picker omits the property.
     */
    customerServiceRepresentativeId: uuid().optional(),
  }),
);
export type OrderActionSubmitSchemaType = z.infer<typeof OrderActionSubmitSchema>;

export const OrderItemsUpdateSchema = z.object({
  id: requiredUuid,
  productItems: z.array(OrderItemProductWithIdSchema).min(1, { message: "An order must have at least 1 line item." }),
});
export type OrderItemsUpdateSchemaType = z.infer<typeof OrderItemsUpdateSchema>;

export const OrderItemAppendSchema = z.object({
  id: requiredUuid,
  expectedUpdatedAt: z.date(),
  item: OrderItemProductSchema.omit({ lotId: true }),
});
export type OrderItemAppendSchemaType = z.infer<typeof OrderItemAppendSchema>;

export const OrderNonProductItemsUpdateSchema = z.object({
  id: requiredUuid,
  nonProductItems: z.array(OrderItemNonProductWithIdSchema),
});
export type OrderNonProductItemsUpdateSchemaType = z.infer<typeof OrderNonProductItemsUpdateSchema>;

export const OrderCostsUpdateSchema = z.object({
  id: requiredUuid,
  costs: z.array(OrderCostWithIdSchema),
});
export type OrderCostsUpdateSchemaType = z.infer<typeof OrderCostsUpdateSchema>;

export const OrderActivityReverseSubmit = OrderActivitySchema;
export type OrderActivityReverseSubmitType = z.infer<typeof OrderActivityReverseSubmit>;

export const OrderActivityReverseShipped = OrderActivitySchema.extend({
  postedAt: z.date(),
});
export type OrderActivityReverseShippedType = z.infer<typeof OrderActivityReverseShipped>;

export const OrderItemTemplateSchema = z
  .object({
    costLedgerAccountId: stringEmptyForcedNull,
    description: z.string().min(1),
    ledgerAccountId: stringEmptyForcedNull,
    packageCost: z.number(),
    packagePrice: z.number().nonnegative(),
    costPricingMode: z.nativeEnum(PricingMode).default(PricingMode.FIXED),
    costPricingPercentageMode: z.nativeEnum(PricingPercentageMode).nullable(),
    costPercentage: z.number().nonnegative().nullable(),
    itemPricingMode: z.nativeEnum(PricingMode).default(PricingMode.FIXED),
    itemPricingPercentageMode: z.nativeEnum(PricingPercentageMode).nullable(),
    itemPercentage: z.number().nonnegative().nullable(),
    /** Default value for nonCommissionable when creating items/costs from this template */
    nonCommissionable: z.boolean().default(false),
    /** Default value for excludeFromMargin when creating items/costs from this template */
    excludeFromMargin: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.costPricingMode === PricingMode.PERCENTAGE) {
      if (data.costPricingPercentageMode == null) {
        ctx.addIssue({
          path: ["costPricingPercentageMode"],
          code: z.ZodIssueCode.custom,
          message: "Percentage mode is required when using percentage pricing.",
        });
      }

      if (data.costPercentage == null || data.costPercentage <= 0) {
        ctx.addIssue({
          path: ["costPercentage"],
          code: z.ZodIssueCode.custom,
          message: "Percentage must be greater than 0 when using percentage pricing.",
        });
      }
    }

    if (data.itemPricingMode === PricingMode.PERCENTAGE) {
      if (data.itemPricingPercentageMode == null) {
        ctx.addIssue({
          path: ["itemPricingPercentageMode"],
          code: z.ZodIssueCode.custom,
          message: "Percentage mode is required when using percentage pricing.",
        });
      }

      if (data.itemPercentage == null || data.itemPercentage <= 0) {
        ctx.addIssue({
          path: ["itemPercentage"],
          code: z.ZodIssueCode.custom,
          message: "Percentage must be greater than 0 when using percentage pricing.",
        });
      }
    }
  });
export const OrderItemTemplateWithIdSchema = PK.and(OrderItemTemplateSchema);

export const OrderActivityConvertToDsoSchema = OrderActivitySchema.merge(
  z.object({
    /** Optional, non-authorizing client acknowledgement of currently displayed warning Flags. */
    acknowledgedRegulatoryWarningRuleIds: z
      .array(uuid())
      .refine(ruleIds => new Set(ruleIds).size === ruleIds.length, { message: "Regulatory warning rule IDs must be unique." })
      .optional(),
    supplierId: requiredUuid,
    supplierTermsOfPaymentId: requiredUuid,
    poFreightTerms: stringEmptyForcedNull,
    shipFromLocationId: z.union([requiredUuid, z.literal(null)]),
    items: z
      .array(
        z.object({
          itemId: requiredUuid,
          itemPackageCost: z.number().nonnegative(),
        }),
      )
      .min(1),
  }),
);
export type OrderActivityConvertToDsoSchemaType = z.infer<typeof OrderActivityConvertToDsoSchema>;

export const OrderActivityConvertToSoSchema = OrderActivitySchema.merge(
  z.object({
    poOperation: z.union([z.literal("void"), z.literal("keep")]),
    poWarehouseId: z.union([z.literal(null), requiredUuid]),
    soWarehouseId: requiredUuid,
    items: z.array(
      PK.merge(
        z.object({
          lots: z.array(OrderActivityLotSchemaLotRecord),
          lotNumber: z.string().trim().optional(), // For "keep PO" - lot number for newly created lot
        }),
      ),
    ),
  }),
)
  .refine(record => record.poOperation !== "keep" || record.poWarehouseId !== null, {
    message: "Warehouse required when keeping the Purchase Order.",
    path: ["poOperation"],
  })
  .refine(record => record.poOperation !== "void" || record.poWarehouseId === null, {
    message: "Warehouse not required when voiding the Purchase Order.",
    path: ["poOperation"],
  })
  .refine(
    record => {
      if (record.poOperation !== "keep") {
        return true;
      }
      return record.items.every(item => {
        const lotNumber = item.lotNumber?.trim();
        return lotNumber && !isReservedLotName(lotNumber);
      });
    },
    {
      message: 'Lot numbers are required when keeping the Purchase Order. "UNSPECIFIED" and "UNASSIGNED" are not allowed.',
      path: ["items"],
    },
  );
export type OrderActivityConvertToSoSchemaType = z.infer<typeof OrderActivityConvertToSoSchema>;

/** Valid UI column accessors that can drive CSV export ordering. */
export const salesOrderExportAccessor = z.enum([
  "orderNo",
  "status",
  "paymentStatus",
  "tags",
  "_latest_comment",
  "_sent",
  "customer.name",
  "assignedToId",
  "products",
  "productCodes",
  "orderKPI.totalVolume",
  "customerOrderNo",
  "salesPersonId",
  "customerServiceRepresentativeId",
  "createdAt",
  "orderedAt",
  "shipBy",
  "requiredBy",
  "orderKPI.totalSale",
  "orderKPI.totalCost",
  "orderKPI.totalProfit",
  "orderKPI.totalMarginPercent",
  "shipToAddress.state",
  "shipToAddress.city",
  "shipToAddress.country",
  "_invoices",
  "remainingBillableAmount",
  "freightMethod",
  "freightTerms",
  "incoterm",
  "shippingCarrier",
  "shippingFob",
  "type",
  "doc_file_counts",
  "costs",
]);
